/**
 * ODC — Campaign detail workspace.
 *
 * Three-column layout:
 *   • LEFT   — opportunity summary + match controls
 *   • CENTER — recipients list with score + status + actions
 *   • RIGHT  — email preview (subject + body editor) for selected recipient
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Sparkles, Send, RefreshCw, CheckCircle2, XCircle,
  Pencil, BarChart3, Eye, AlertCircle, Wand2,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  proposed:  'bg-gray-100 text-gray-700 border-gray-200',
  approved:  'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected:  'bg-red-100 text-red-700 border-red-300',
  sent:      'bg-purple-100 text-purple-700 border-purple-300',
  bounced:   'bg-amber-100 text-amber-700 border-amber-300',
};

const scoreColor = (s) => (s >= 70 ? 'text-emerald-600' : s >= 45 ? 'text-amber-600' : 'text-gray-500');

const OdcCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [opp, setOpp] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [sending, setSending] = useState(false);
  const [scoreThreshold, setScoreThreshold] = useState(35);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [editor, setEditor] = useState({ subject: '', body: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const o = await axios.get(`${API_URL}/api/odc/opportunities/${id}`, { headers });
      setOpp(o.data);
      try {
        const c = await axios.get(`${API_URL}/api/odc/opportunities/${id}/campaign`, { headers });
        setCampaign(c.data);
        if (c.data.recipients?.length && !selectedClientId) {
          setSelectedClientId(c.data.recipients[0].client_id);
        }
      } catch (e) {
        if (e.response?.status === 404) setCampaign(null);
        else throw e;
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Load failed', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  const selectedRecipient = useMemo(
    () => campaign?.recipients?.find((r) => r.client_id === selectedClientId) || null,
    [campaign, selectedClientId]
  );

  // Sync editor when selected recipient changes
  useEffect(() => {
    if (selectedRecipient) {
      setEditor({
        subject: selectedRecipient.subject_draft || '',
        body: selectedRecipient.body_html_draft || '',
      });
    }
  }, [selectedClientId, selectedRecipient]);

  const runMatch = async () => {
    setMatching(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/odc/opportunities/${id}/match?score_threshold=${scoreThreshold}`,
        {},
        { headers, timeout: 90000 }
      );
      setCampaign(r.data);
      toast({ title: 'Matched', description: `${r.data.recipients.length} candidate recipient(s) generated.` });
      await fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Match failed', description: e.response?.data?.detail || e.message });
    } finally {
      setMatching(false);
    }
  };

  const patchRecipient = async (clientId, patch) => {
    try {
      const r = await axios.patch(`${API_URL}/api/odc/campaigns/${campaign.id}/recipients/${clientId}`, patch, { headers });
      setCampaign(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Update failed', description: e.response?.data?.detail || e.message });
    }
  };

  const saveEditor = async () => {
    if (!selectedRecipient) return;
    setSavingEdit(true);
    await patchRecipient(selectedRecipient.client_id, {
      subject_draft: editor.subject,
      body_html_draft: editor.body,
    });
    setSavingEdit(false);
    toast({ title: 'Draft saved' });
  };

  const regenerateAI = async () => {
    if (!selectedRecipient) return;
    setSavingEdit(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/odc/campaigns/${campaign.id}/recipients/${selectedRecipient.client_id}/regenerate`,
        {},
        { headers, timeout: 60000 }
      );
      setCampaign(r.data);
      toast({ title: 'AI rewrote the draft' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'AI failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSavingEdit(false);
    }
  };

  const bulkAction = async (action) => {
    try {
      const body = action.startsWith('approve_above') || action.startsWith('reject_below')
        ? { action, threshold: scoreThreshold }
        : { action };
      const r = await axios.post(`${API_URL}/api/odc/campaigns/${campaign.id}/bulk-action`, body, { headers });
      setCampaign(r.data);
      toast({ title: `Bulk action: ${action}` });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Bulk action failed', description: e.response?.data?.detail || e.message });
    }
  };

  const sendCampaign = async () => {
    const approved = campaign.recipients.filter((r) => r.status === 'approved' && !r.sent_at);
    if (!approved.length) {
      toast({ variant: 'destructive', title: 'Nothing to send', description: 'Approve at least one recipient first.' });
      return;
    }
    if (!window.confirm(`Send to ${approved.length} approved recipient(s)?\n\nThis will dispatch real emails via SendGrid.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/odc/campaigns/${campaign.id}/send`, {}, { headers });
      toast({ title: 'Campaign dispatched', description: `${r.data.dispatched} email(s) queued.` });
      setTimeout(fetchAll, 1500);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Send failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-gray-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading opportunity…
      </div>
    );
  }
  if (!opp) return null;

  const recipients = campaign?.recipients || [];
  const counts = {
    proposed: recipients.filter((r) => r.status === 'proposed').length,
    approved: recipients.filter((r) => r.status === 'approved').length,
    rejected: recipients.filter((r) => r.status === 'rejected').length,
    sent: recipients.filter((r) => r.status === 'sent').length,
  };

  return (
    <div className="space-y-4" data-testid="odc-campaign-detail">
      <Link to="/admin/odc" className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to ODC
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT — opportunity summary + match controls */}
        <Card className="lg:col-span-3">
          <CardContent className="pt-5 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{opp.code}</div>
              <h2 className="text-lg font-bold text-[#0F172A]">{opp.title}</h2>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">{opp.summary}</p>
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
              <div><b>Target:</b> {opp.country_target || '—'}</div>
              <div><b>Value:</b> {opp.currency} {opp.estimated_value ? Number(opp.estimated_value).toLocaleString() : '—'}</div>
              <div><b>Deadline:</b> {opp.deadline || '—'}</div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {(opp.industry_tags || []).map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <label className="text-xs uppercase text-gray-500">Score threshold</label>
              <input
                type="range" min={0} max={100} value={scoreThreshold}
                onChange={(e) => setScoreThreshold(Number(e.target.value))}
                className="w-full"
                data-testid="odc-threshold-slider"
              />
              <div className="text-xs text-[#0F172A] font-mono">{scoreThreshold}</div>
              <Button onClick={runMatch} disabled={matching} size="sm" className="w-full bg-[#0F172A] text-white" data-testid="odc-run-match">
                {matching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {campaign ? 'Re-run match' : 'Run match'}
              </Button>
            </div>
            {campaign && (
              <div className="border-t pt-3 space-y-2">
                <div className="text-xs uppercase text-gray-500">Bulk actions</div>
                <Button onClick={() => bulkAction('approve_above')} size="sm" variant="outline" className="w-full" data-testid="bulk-approve-above">
                  <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" /> Approve ≥ {scoreThreshold}
                </Button>
                <Button onClick={() => bulkAction('reject_below')} size="sm" variant="outline" className="w-full">
                  <XCircle className="w-3 h-3 mr-1 text-red-500" /> Reject &lt; {scoreThreshold}
                </Button>
              </div>
            )}
            {campaign && (
              <div className="border-t pt-3">
                <Button
                  onClick={sendCampaign}
                  disabled={sending || !counts.approved}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="odc-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Send Approved ({counts.approved})
                </Button>
                {opp.status === 'sent' && (
                  <div className="mt-2 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Campaign sent
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CENTER — recipients list */}
        <Card className="lg:col-span-5">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-[#0F172A] inline-flex items-center gap-2">
                Recipients <Badge variant="outline">{recipients.length}</Badge>
              </div>
              {campaign && (
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
                  <span className="text-gray-500">proposed {counts.proposed}</span>
                  <span className="text-emerald-600">approved {counts.approved}</span>
                  <span className="text-red-500">rejected {counts.rejected}</span>
                  {counts.sent > 0 && <span className="text-purple-600">sent {counts.sent}</span>}
                </div>
              )}
            </div>
            {!campaign ? (
              <div className="text-sm text-gray-500 text-center py-12 border border-dashed rounded">
                <Sparkles className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                Run match to score CRM clients against this opportunity.
              </div>
            ) : recipients.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-10">
                No candidates above the threshold. Try lowering the score.
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto space-y-1.5 pr-1">
                {recipients.map((r) => (
                  <div
                    key={r.client_id}
                    onClick={() => setSelectedClientId(r.client_id)}
                    data-testid={`recipient-${r.client_id}`}
                    className={`p-2.5 rounded border cursor-pointer transition-all ${
                      selectedClientId === r.client_id
                        ? 'border-[#00B4D8] bg-[#00B4D8]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm text-[#0F172A] truncate flex-1">
                        {r.client_company_name}
                      </div>
                      <div className={`font-mono text-sm font-bold ${scoreColor(r.match_score)}`}>
                        {Math.round(r.match_score)}
                      </div>
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {r.client_email} · {r.client_industry || 'no industry'}
                    </div>
                    {r.match_rationale && (
                      <div className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-1">{r.match_rationale}</div>
                    )}
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${STATUS_PILL[r.status] || ''}`}>
                        {r.status}
                      </span>
                      {r.edited && <Badge variant="outline" className="text-[9px] py-0">edited</Badge>}
                      {r.opens > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] py-0"><Eye className="w-2.5 h-2.5 mr-0.5" /> {r.opens}</Badge>}
                      <div className="ml-auto flex items-center gap-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); patchRecipient(r.client_id, { status: 'approved' }); }}
                          className="text-emerald-600 hover:bg-emerald-50 rounded p-0.5"
                          data-testid={`approve-${r.client_id}`}
                          title="Approve"
                        ><CheckCircle2 className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={(e) => { e.stopPropagation(); patchRecipient(r.client_id, { status: 'rejected' }); }}
                          className="text-red-500 hover:bg-red-50 rounded p-0.5"
                          title="Reject"
                        ><XCircle className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* RIGHT — email preview / editor */}
        <Card className="lg:col-span-4">
          <CardContent className="pt-5">
            <div className="text-sm font-bold text-[#0F172A] inline-flex items-center gap-2 mb-3">
              <Pencil className="w-4 h-4" /> Email Draft
            </div>
            {!selectedRecipient ? (
              <div className="text-sm text-gray-500 text-center py-10 border border-dashed rounded">
                Select a recipient to edit the email draft.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[11px] text-gray-500">
                  To: <b>{selectedRecipient.client_email}</b>
                </div>
                <div>
                  <label className="text-xs uppercase text-gray-500">Subject</label>
                  <input
                    value={editor.subject}
                    onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded text-sm"
                    data-testid="editor-subject"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase text-gray-500">Body HTML</label>
                  <textarea
                    value={editor.body}
                    onChange={(e) => setEditor({ ...editor, body: e.target.value })}
                    rows={10}
                    className="mt-1 w-full px-3 py-2 border rounded text-xs font-mono"
                    data-testid="editor-body"
                  />
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 mb-1">Preview</div>
                  <div
                    className="border rounded p-3 max-h-48 overflow-y-auto text-sm bg-white"
                    dangerouslySetInnerHTML={{ __html: editor.body }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveEditor} disabled={savingEdit} size="sm" className="flex-1 bg-[#0F172A] text-white" data-testid="editor-save">
                    {savingEdit ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Pencil className="w-3.5 h-3.5 mr-1" />}
                    Save draft
                  </Button>
                  <Button onClick={regenerateAI} disabled={savingEdit} size="sm" variant="outline" data-testid="editor-ai-rewrite">
                    <Wand2 className="w-3.5 h-3.5 mr-1" /> Rewrite with AI
                  </Button>
                </div>
                {selectedRecipient.score_breakdown && (
                  <div className="border-t pt-2 text-[11px] text-gray-500">
                    <div className="inline-flex items-center gap-1 mb-1"><BarChart3 className="w-3 h-3" /> Score breakdown</div>
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(selectedRecipient.score_breakdown).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="capitalize">{k}</span>
                          <span className={`font-mono ${scoreColor(v)}`}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OdcCampaignDetail;
